import {
	BookOutlined,
	CalendarOutlined,
	FileTextOutlined,
	SettingOutlined,
	TeamOutlined,
	UserOutlined,
} from "@ant-design/icons";
import { Badge, Menu, Typography } from "antd";
import { ItemType } from "antd/lib/menu/hooks/useItems";
import React from "react";
import { Database } from "react-feather";
import { Link, useLocation } from "react-router-dom";
import { ReactComponent as Building } from "../../assets/Icons/Building.svg";
import { ReactComponent as Home } from "../../assets/Icons/Home.svg";
import { ReactComponent as HomeCheck } from "../../assets/Icons/HomeCheck.svg";
import { ReactComponent as Transfer } from "../../assets/Icons/Transfer.svg";
import { ReactComponent as UserCircle } from "../../assets/Icons/UserCircle.svg";
import { ReactComponent as UserGroup } from "../../assets/Icons/UserGroup.svg";
import { ReactComponent as UserIdCard } from "../../assets/Icons/UserIdCard.svg";
import { ReactComponent as UserSquare } from "../../assets/Icons/UserSquare.svg";
import { useRemoteConfig } from "../../firebase/RemoteConfigProvider";
import useAuth from "../../hooks/useAuth";
import { EEmployeeRole } from "../../types/employee.type";
import { CustomItemType } from "./sidebar.type";

const SidebarMenu: React.FC = () => {
	const location = useLocation();
	const {
		isHavePermission,
		isAdminHQPlus,
		isHQ,
		isHQManagerPlus,
		isFOPlus,
		isBOPlus,
		isAdminHQWithFinancePlus,
		isAdminHQWithOfficeFinancePlusAndAgent,
		isAdminHQOfficeFinancePlus,
		isAdminHQAndAdminOffices,
		isAdminBlog,
		isAgent,
		roles,
		isSuperAdmin,
		isFranchiseOrBusinessAdmin,
		isAdminOrAdminHQ,
		isHQAndOfficeManagementNonFinance,
	} = useAuth();
	const { readConfig } = useRemoteConfig();

	const showMenuClient =
		process.env.REACT_APP_STAGE === "staging" || process.env.REACT_APP_STAGE === "development"
			? true
			: process.env.REACT_APP_STAGE === "production" &&
			  readConfig("hidden_features").includes("sidebar_menu_client")
			? false
			: true;

	const showMenuTemplateMessage =
		process.env.REACT_APP_STAGE === "staging" || process.env.REACT_APP_STAGE === "development"
			? true
			: process.env.REACT_APP_STAGE === "production" &&
			  readConfig("hidden_features").includes("sidebar_menu_template_message")
			? false
			: true;

	const showMenuListingNoWatermark =
		process.env.REACT_APP_STAGE === "staging" || process.env.REACT_APP_STAGE === "development"
			? true
			: process.env.REACT_APP_STAGE === "production" &&
			  readConfig("hidden_features").includes("sidebar_menu_listing_no_watermark")
			? false
			: true;

	const showMenuListingHasPortal =
		process.env.REACT_APP_STAGE === "staging" || process.env.REACT_APP_STAGE === "development"
			? true
			: process.env.REACT_APP_STAGE === "production" &&
			  readConfig("hidden_features").includes("sidebar_menu_listing_has_portal")
			? false
			: true;

	const showMenuMyList =
		process.env.REACT_APP_STAGE === "staging" || process.env.REACT_APP_STAGE === "development"
			? true
			: process.env.REACT_APP_STAGE === "production" &&
			  readConfig("hidden_features").includes("sidebar_menu_mylist")
			? false
			: true;

	const showMenuOfficeTeams =
		process.env.REACT_APP_STAGE === "staging" || process.env.REACT_APP_STAGE === "development"
			? true
			: process.env.REACT_APP_STAGE === "production" &&
			  readConfig("hidden_features").includes("sidebar_menu_office_teams")
			? false
			: true;

	const filterPermission = (items: CustomItemType[]) => {
		return items
			.map((item) => {
				if (!item) return null;

				if (item?.permission === undefined || item.permission === true) {
					let newObject = item;
					delete item.permission;
					if ("children" in newObject && newObject.children) {
						newObject.children = filterPermission(newObject.children);
					}
					if (!("children" in newObject) || !newObject.children || newObject.children.length > 0) {
						return newObject;
					} else {
						return null;
					}
				} else {
					return null;
				}
			})
			.filter(Boolean);
	};

	const removeSingleSubmenu = (items: ItemType[]) => {
		return items.map((item) => {
			if (!item) return null;

			if ("children" in item && item.children) {
				return {
					...item,
					children: item.children.map((menu) => {
						if (!menu) return null;

						if ("children" in menu && menu.children) {
							if (menu.children.length === 1 && menu.children[0]?.key) {
								return {
									...menu,
									key: menu.children[0].key,
									children: undefined,
								};
							} else {
								return menu;
							}
						} else {
							return menu;
						}
					}),
				};
			} else {
				return item;
			}
		});
	};

	const formatLink = (items: ItemType[]): ItemType[] => {
		let result = items.map((item) => {
			if (!item) return null;
			if (!("label" in item)) return item;

			if ("children" in item && item.children) {
				return {
					...item,
					children: formatLink(item.children),
				};
			}

			return {
				...item,
				label: <Link to={item.key as string}>{item.label}</Link>,
			};
		});
		return result;
	};

	const MENUS: CustomItemType[] =
		roles.length === 1 && roles[0] === EEmployeeRole.admin_blog
			? [
					{
						label: "Dashboard",
						icon: <Home />,
						key: "/dashboard",
					},
					{
						type: "group",
						label: "BLOG",
						permission: isAdminBlog,
						children: [
							{
								label: "Remax Blog",
								icon: <BookOutlined />,
								key: "blog",
								children: [
									{
										label: "All Posts",
										key: "/blog-posts",
									},
									{
										label: "Categories",
										key: "/blog-categories",
									},
									{
										label: "Tags",
										key: "/blog-tags",
									},
								],
							},
						],
					},
			  ]
			: [
					{
						label: "Dashboard",
						icon: <Home />,
						key: "/dashboard",
					},
					{
						permission: isHQManagerPlus,
						type: "group",
						label: "HQ INFORMATION",
						children: [
							{
								label: "HQ",
								key: "/hq",
								icon: <UserSquare />,
							},
						],
					},
					{
						permission: isAdminHQPlus,
						type: "group",
						label: "FRANCHISE OWNER",
						children: [
							{
								permission: isAdminHQPlus,
								label: "Franchise Owner",
								key: "fo",
								icon: <UserCircle />,
								children: [
									{
										label: "Data Franchise Owner",
										key: "/franchise-owner",
									},
									{
										permission: isHQManagerPlus,
										label: "Approve Franchise Owner",
										key: "/approve-franchise-owner",
									},
								],
							},
						],
					},
					{
						type: "group",
						label: "OFFICE INFORMATION",
						children: [
							isFOPlus
								? {
										label: "Office",
										key: "office",
										icon: <Building />,
										children: [
											{
												permission: isFOPlus,
												label: "Data Office",
												key: "/office",
											},
											{
												permission: isHQManagerPlus,
												label: "Approve Office",
												key: "/approve-office",
											},
											// {
											// 	permission: showMenuOfficeTeams,
											// 	label: (
											// 		<Badge
											// 			dot={readConfig("hidden_features").includes(
											// 				"sidebar_menu_office_teams"
											// 			)}
											// 			color="yellow"
											// 			offset={[8, 10]}
											// 		>
											// 			<Typography
											// 				style={{
											// 					color: "white",
											// 				}}
											// 			>
											// 				Office Teams
											// 			</Typography>
											// 		</Badge>
											// 	),
											// 	key: "teams",
											// 	children: [
											// 		{
											// 			label: "Data Team",
											// 			key: "/teams",
											// 		},
											// 		{
											// 			label: "Transfer Team Member",
											// 			key: "/transfer-team-member",
											// 			permission: isAdminHQAndAdminOffices,
											// 		},
											// 	],
											// },
										],
								  }
								: isAdminHQAndAdminOffices
								? {
										label: isHavePermission(EEmployeeRole.admin_office) ? "Office" : "Office Teams",
										key: isHavePermission(EEmployeeRole.admin_office) ? "office" : "teams",
										icon: <Building />,
										children: [
											{
												label: "Detail Office",
												key: "/detail-office",
												permission: isHavePermission(EEmployeeRole.admin_office),
											},
											// {
											// 	label: (
											// 		<Badge
											// 			dot={readConfig("hidden_features").includes(
											// 				"sidebar_menu_office_teams"
											// 			)}
											// 			color="yellow"
											// 			offset={[8, 10]}
											// 		>
											// 			<Typography
											// 				style={{
											// 					color: "white",
											// 				}}
											// 			>
											// 				Data Team
											// 			</Typography>
											// 		</Badge>
											// 	),
											// 	key: "/teams",
											// 	permission: showMenuOfficeTeams,
											// },
											// {
											// 	label: (
											// 		<Badge
											// 			dot={readConfig("hidden_features").includes(
											// 				"sidebar_menu_office_teams"
											// 			)}
											// 			color="yellow"
											// 			offset={[8, 10]}
											// 		>
											// 			<Typography
											// 				style={{
											// 					color: "white",
											// 				}}
											// 			>
											// 				Transfer Team Member
											// 			</Typography>
											// 		</Badge>
											// 	),
											// 	key: "/transfer-team-member",
											// 	permission: isAdminHQAndAdminOffices && showMenuOfficeTeams,
											// },
										],
								  }
								: {
										label: (
											<Badge
												dot={readConfig("hidden_features").includes(
													"sidebar_menu_office_teams"
												)}
												color="yellow"
												offset={[8, 10]}
											>
												<Typography
													style={{
														color: "white",
													}}
												>
													Office Teams
												</Typography>
											</Badge>
										),
										key: "teams",
										permission: showMenuOfficeTeams,
										icon: <Building />,
										children: [
											{
												label: "Data Team",
												key: "/teams",
											},
										],
								  },
							{
								permission: isBOPlus,
								label: "Office Staff",
								key: "staff",
								icon: <UserIdCard />,
								children: [
									{
										label: "Data Staff",
										key: "/staff",
									},
									{
										permission: isHQManagerPlus,
										label: "Approve Staff",
										key: "/approve-staff",
									},
								],
							},
							{
								// permission: isAdminOfficePlus,
								permission: isAdminHQAndAdminOffices,
								label: "Agent",
								key: "agent",
								icon: <UserGroup />,
								children: [
									{
										label: "Data Agent",
										key: "/agent",
									},
									{
										permission: isAdminHQPlus,
										label: "Approve Agent",
										key: "/approve-agent",
									},
									{
										permission: isFranchiseOrBusinessAdmin,
										label: "Setting Agent",
										key: "/setting-agent",
									},
									// {
									// 	permission: isHQAndOfficeManagementNonFinance,
									// 	label: "Transfer Listing",
									// 	key: "/transfer-listing",
									// },
								],
							},
							// {
							// 	permission: isFranchiseOrBusinessAdmin,
							// 	label: "Setting Agent",
							// 	key: "setting-agent",
							// 	icon: <UserGroup />,
							// 	children: [
							// 		{
							// 			label: "Setting Agent",
							// 			key: "/setting-agent",
							// 		},
							// 	],
							// },
							{
								label: "Listing",
								key: "listing",
								icon: <HomeCheck />,
								children: [
									{
										label: "Data Listing",
										key: "/listing",
									},
									{
										label: "Data Masking",
										key: "/masking",
									},
									{
										permission: showMenuMyList,
										// permission: showMenuMyList && isAgent,
										// label: "MyList",
										label: (
											<Badge
												dot={readConfig("hidden_features").includes("sidebar_menu_mylist")}
												color="yellow"
												offset={[5, 8]}
											>
												<Typography
													style={{
														color: "#fff",
													}}
												>
													MyList
												</Typography>
											</Badge>
										),
										key: "/my-list",
									},
									{
										permission: isAdminHQWithOfficeFinancePlusAndAgent,
										label: "Data Closing",
										key: "/closing",
									},
									{
										label: "Request Board",
										// icon: <SwapOutlined />,
										key: "/request-board",
									},
									{
										permission: !isAdminHQOfficeFinancePlus,
										label: "Primary Project Coordination",
										key: "/primary-project-coordination",
									},
									{
										permission: isAdminHQPlus,
										label: "Data Portal",
										key: "/portal",
									},
									{
										permission: isAdminHQPlus,
										label: "Portal Checker",
										key: "/portal-checker",
									},
									{
										permission: isAdminHQPlus,
										label: "Setting Portal Agent",
										key: "/setting-portal-agent",
									},
									// {
									// 	permission: showMenuListingNoWatermark && isAdminHQPlus,
									// 	label: (
									// 		<Badge
									// 			dot={readConfig("hidden_features").includes(
									// 				"sidebar_menu_listing_no_watermark"
									// 			)}
									// 			color="yellow"
									// 			offset={[7, 5]}
									// 		>
									// 			<Typography
									// 				style={{
									// 					color: "white",
									// 				}}
									// 			>
									// 				Listing Not Watermark
									// 			</Typography>
									// 		</Badge>
									// 	),
									// 	key: "/listing-not-watermark",
									// },
									// {
									// 	permission: showMenuListingHasPortal && isAdminHQPlus,
									// 	label: (
									// 		<Badge
									// 			dot={readConfig("hidden_features").includes(
									// 				"sidebar_menu_listing_has_portal"
									// 			)}
									// 			color="yellow"
									// 			offset={[7, 5]}
									// 		>
									// 			<Typography
									// 				style={{
									// 					color: "white",
									// 				}}
									// 			>
									// 				Listing Has Portal
									// 			</Typography>
									// 		</Badge>
									// 	),
									// 	key: "/listing-has-portal",
									// },
								],
							},
							// {
							// 	permission: isAdminHQPlus,
							// 	label: "Transfer Staff & Agent",
							// 	key: "/transfer-staff",
							// 	icon: <Transfer />,
							// },
						],
					},
					{
						type: "group",
						label: "TRANSFER",
						children: [
							{
								permission: isHQAndOfficeManagementNonFinance,
								label: "Transfer Listing",
								key: "/transfer-listing",
								icon: <Transfer />,
							},
							{
								permission: isAdminHQPlus,
								label: "Transfer Staff & Agent",
								key: "/transfer-staff",
								icon: <Transfer />,
							},
						],
					},
					{
						type: "group",
						label: "TRANSACTION",
						children: [
							{
								label: isAdminHQWithOfficeFinancePlusAndAgent ? "Transaction" : "Handover",
								icon: <FileTextOutlined />,
								key: "transaction",
								children: [
									{
										permission: isAdminHQWithOfficeFinancePlusAndAgent,
										label: "Invoice",
										key: "/invoice",
									},
									{
										permission: isAdminHQWithOfficeFinancePlusAndAgent,
										label: "Agent Receivable",
										key: "/agent-receivable",
									},
									{
										permission: isAdminHQWithFinancePlus,
										label: "Billing",
										key: "/billing",
									},
									{
										permission: !isAdminHQOfficeFinancePlus,
										label: "Handover",
										key: "/handover",
									},
								],
							},
						],
					},
					{
						// permission: !isAdminHQOfficeFinancePlus,
						type: "group",
						label: "EVENT & ANNOUNCEMENT",
						children: [
							{
								label: "Event & Training",
								icon: <CalendarOutlined />,
								key: "/event",
							},
						],
					},
					{
						type: "group",
						label: "BLOG",
						permission: isAdminBlog,
						children: [
							{
								label: "Remax Blog",
								icon: <BookOutlined />,
								key: "blog",
								children: [
									{
										label: "All Posts",
										key: "/blog-posts",
									},
									{
										label: "Categories",
										key: "/blog-categories",
									},
									{
										label: "Tags",
										key: "/blog-tags",
									},
									{
										label: "Blog Author",
										key: "/author",
										permission: isAdminHQPlus,
									},
								],
							},
						],
					},
					{
						type: "group",
						label: "MASTER DATA",
						children: [
							{
								permission: isAdminHQPlus,
								label: "Master Data",
								key: "master-data",
								icon: <Database size="1em" />,
								children: [
									{
										label: "Achievement & Awards",
										key: "/master-data-achievement",
									},
									{
										label: "Document Center",
										key: "/document-center",
									},
									{
										label: "E-Magazine",
										key: "/magazine",
									},
									{
										label: "Career Path",
										key: "/master-data-career",
									},
									{
										label: "Fee",
										key: "/master-data-fee",
									},
									{
										label: "Property Type",
										key: "/master-data-property-types",
									},
									{
										label: "Partner",
										key: "/partner",
									},
									{
										label: "Group of City",
										key: "/group-of-city",
									},
								],
							},
							{
								permission: isAdminHQPlus,
								label: "Web Config",
								key: "web-config",
								icon: <SettingOutlined />,
								children: [
									{
										label: "Banner",
										key: "/banner",
									},
									{
										label: "Album",
										key: "/album",
									},
									{
										label: "Preference",
										key: "/preference",
									},
									{
										permission: showMenuTemplateMessage && isAdminOrAdminHQ,
										label: (
											<Badge
												dot={readConfig("hidden_features").includes(
													"sidebar_menu_template_message"
												)}
												color="yellow"
												offset={[7, 5]}
											>
												<Typography
													style={{
														color: "white",
													}}
												>
													Template Message
												</Typography>
											</Badge>
										),
										key: "/template-messages",
									},
									{
										label: "Config App Logo",
										key: "/config-app-logo",
									},

									{
										label: "App Config",
										key: "/app-config",
									},
								],
							},
						],
					},
					{
						permission: showMenuClient && (isAgent || roles.includes(EEmployeeRole.admin)),
						type: "group",
						label: (
							<Badge
								dot={readConfig("hidden_features").includes("sidebar_menu_client")}
								color="yellow"
								offset={[7, 5]}
							>
								<Typography
									style={{
										color: "#748494",
									}}
								>
									CLIENT
								</Typography>
							</Badge>
						),
						children: [
							{
								label: "Client",
								icon: <TeamOutlined />,
								key: "/clients",
							},
						],
					},
					{
						type: "group",
						label: "Users",
						children: [
							{
								permission: isHavePermission(EEmployeeRole.admin),
								label: "Manage User",
								icon: <UserOutlined />,
								key: "/users",
							},
							{
								permission: isHavePermission(EEmployeeRole.admin),
								label: "Manage Public User",
								icon: <UserOutlined />,
								key: "/public-users",
							},
							{
								permission: isHQ,
								label: "Login Activity",
								icon: <UserOutlined />,
								key: "/login-activity",
							},
						],
					},
			  ];

	const items = formatLink(removeSingleSubmenu(filterPermission(MENUS)));

	const convertPathName = () => {
		const pathname = location.pathname.split("/");
		const res = "/" + pathname[1];
		return res;
	};

	const getSelectedParent = () => {
		const currentMenu = convertPathName();
		var parent: string[] = [];
		if (items) {
			for (const group of items) {
				if (group && "children" in group && group.children) {
					for (const submenu of group.children) {
						var current = String(submenu?.key ? submenu.key : "");
						if (submenu && "children" in submenu && submenu.children) {
							for (const item of submenu.children) {
								if (
									item &&
									"key" in item &&
									(item as any).key === currentMenu &&
									current !== undefined
								) {
									parent.push(current);
								}
							}
						}
					}
				}
			}
		}
		return parent;
	};

	return (
		<Menu
			style={{ paddingBottom: 100 }}
			mode="inline"
			selectedKeys={[convertPathName()]}
			defaultOpenKeys={getSelectedParent()}
			items={items}
		/>
	);
};

export default SidebarMenu;
